import { LogOut, MapPin, Stethoscope } from 'lucide-react';

import { useMyEmployeeRecordQuery } from '@/entities/me';
import { getUsStateCodeByName } from '@/shared/lib';
import { Button } from '@/shared/ui/shadcn/button';

function getInitials(displayName: string): string {
  return displayName
    .split(' ')
    .map((part) => part[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function toAbbreviation(fullStateName: string): string {
  return getUsStateCodeByName(fullStateName) ?? fullStateName;
}

interface ClinicianProfileCardProps {
  onSignOut: () => void;
}

export function ClinicianProfileCard({ onSignOut }: ClinicianProfileCardProps) {
  const employeeRecordQuery = useMyEmployeeRecordQuery();
  const record = employeeRecordQuery.data ?? null;

  const displayName = record?.displayName?.trim() || 'Clinician';
  const initials = record != null ? getInitials(displayName) : null;

  const licensedStates =
    record != null
      ? (record.licensedStates ?? []).map((s) => s.trim()).filter((s) => s !== '')
      : null;
  const practiceAreas =
    record != null
      ? (record.licensedPracticeAreas ?? []).map((s) => s.trim()).filter((s) => s !== '')
      : null;
  const stateSummary = licensedStates?.map((state) => toAbbreviation(state)).join(', ') ?? null;
  const practiceAreaSummary = practiceAreas?.join(', ') ?? null;

  return (
    <div className="rounded-xl border border-sidebar-border/80 bg-sidebar-accent/35 p-2.5">
      <div className="flex items-center gap-2">
        {initials != null ? (
          <div
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary"
            aria-hidden="true"
          >
            {initials}
          </div>
        ) : (
          <div
            className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-muted/50"
            aria-hidden="true"
          />
        )}
        <div className="min-w-0 flex-1">
          {record != null ? (
            <>
              <p className="truncate text-sm font-medium text-sidebar-foreground">{displayName}</p>
              <div className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground">
                <MapPin className="h-3 w-3 shrink-0" aria-hidden="true" />
                <span className="truncate">
                  {stateSummary !== '' ? stateSummary : 'No licensed states'}
                </span>
              </div>
            </>
          ) : (
            <div className="space-y-1.5">
              <div className="h-2.5 w-24 animate-pulse rounded-full bg-muted/50" />
              <div className="h-2 w-16 animate-pulse rounded-full bg-muted/40" />
            </div>
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0 rounded-full text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground"
          aria-label="Log out"
          title="Log out"
          onClick={onSignOut}
        >
          <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      </div>

      {record != null ? (
        <div className="mt-1.5 flex items-center gap-1 text-[11px] text-muted-foreground">
          <Stethoscope className="h-3 w-3 shrink-0" aria-hidden="true" />
          <span className="truncate">
            {practiceAreaSummary !== '' ? practiceAreaSummary : 'No practice areas'}
          </span>
        </div>
      ) : null}
    </div>
  );
}
